<template>
    <div class="rf-shared">
        <div class="row">
            <div class="col-md-6 form-group">
                <label class="form-control-label">{{ $t("marketing.referral.field.team") }}</label>
                <el-select v-model="form.team" class="w-100" filterable allow-create default-first-option clearable :disabled="disabled" :placeholder="$t('marketing.referral.datetime_placeholder')" data-testid="rf-team">
                    <el-option v-for="t in teams" :key="t" :value="t" :label="t"></el-option>
                </el-select>
                <small v-if="errors.team" class="text-danger">{{ err(errors.team) }}</small>
            </div>
            <div class="col-md-6 form-group">
                <label class="form-control-label">{{ $t("marketing.referral.field.campaign") }}</label>
                <el-select v-model="form.campaign" class="w-100" filterable allow-create default-first-option clearable :disabled="disabled" :placeholder="$t('marketing.referral.datetime_placeholder')" data-testid="rf-campaign">
                    <el-option v-for="c in campaigns" :key="c" :value="c" :label="c"></el-option>
                </el-select>
                <small v-if="errors.campaign" class="text-danger">{{ err(errors.campaign) }}</small>
            </div>
        </div>

        <div class="form-group">
            <label class="form-control-label d-block">{{ $t("marketing.referral.field.landing") }}</label>
            <el-radio-group v-model="form.landing.mode" size="small" :disabled="disabled" data-testid="rf-landing-mode">
                <el-radio-button label="home">{{ $t("marketing.referral.field.landing_home") }}</el-radio-button>
                <el-radio-button label="lp">{{ $t("marketing.referral.field.landing_lp") }}</el-radio-button>
                <el-radio-button label="custom">{{ $t("marketing.referral.field.landing_custom") }}</el-radio-button>
            </el-radio-group>
            <el-select v-if="form.landing.mode === 'lp'" v-model="form.landing.lp_slug" class="w-100 mt-2" filterable :disabled="disabled" :placeholder="$t('marketing.referral.lp_placeholder')" data-testid="rf-lp">
                <el-option v-for="lp in lps" :key="lp.slug" :value="lp.slug" :label="lp.title + ' — /lp/' + lp.slug"></el-option>
            </el-select>
            <el-input v-if="form.landing.mode === 'custom'" v-model="form.landing.custom" class="mt-2" maxlength="300" :disabled="disabled" :placeholder="$t('marketing.referral.custom_placeholder')" data-testid="rf-custom"></el-input>
            <small v-if="errors.landing_path" class="text-danger d-block">{{ err(errors.landing_path) }}</small>
        </div>

        <div class="form-group">
            <label class="form-control-label d-block">{{ $t("marketing.referral.field.reward") }}</label>
            <el-radio-group v-model="form.reward_type" :disabled="disabled" data-testid="rf-reward">
                <el-radio label="none">{{ $t("marketing.referral.field.reward_none") }}</el-radio>
                <el-radio label="coupon">{{ $t("marketing.referral.field.reward_coupon") }}</el-radio>
            </el-radio-group>
            <template v-if="form.reward_type === 'coupon'">
                <el-select v-model="form.reward_coupon_template_id" class="w-100 mt-2" filterable :disabled="disabled" :placeholder="$t('marketing.referral.template_placeholder')" data-testid="rf-template">
                    <el-option v-for="t in templateOptions" :key="t.id" :value="t.id" :label="t.label"></el-option>
                </el-select>
                <small v-if="selectedTemplate && selectedTemplate.eligibility === 'new_member'" class="d-block text-warning mt-1" data-testid="rf-welcome-dup">{{ $t("marketing.referral.hint.welcome_dup") }}</small>
                <small v-if="deadTemplate" class="d-block text-danger mt-1">{{ $t("marketing.referral.hint.template_dead") }}</small>
                <small v-if="errors.reward_coupon_template_id" class="d-block text-danger">{{ err(errors.reward_coupon_template_id) }}</small>
            </template>
            <small v-if="rewardChanged" class="d-block text-muted mt-1" data-testid="rf-reward-future">{{ $t("marketing.referral.hint.reward_future") }}</small>
        </div>

        <div class="row">
            <div class="col-md-4 form-group">
                <label class="form-control-label">{{ $t("marketing.referral.field.max_uses") }}</label>
                <el-input v-model="form.max_uses" inputmode="numeric" :disabled="disabled" :placeholder="$t('marketing.referral.field.unlimited')" data-testid="rf-max-uses"></el-input>
                <small v-if="errors.max_uses" class="text-danger">{{ err(errors.max_uses) }}</small>
            </div>
            <div class="col-md-4 form-group">
                <label class="form-control-label">{{ $t("marketing.referral.field.starts_at") }}</label>
                <el-date-picker v-model="form.starts_local" type="datetime" value-format="yyyy-MM-ddTHH:mm:ss" format="yyyy-MM-dd HH:mm" class="w-100" clearable :disabled="disabled" :placeholder="$t('marketing.referral.datetime_placeholder')"></el-date-picker>
            </div>
            <div class="col-md-4 form-group">
                <label class="form-control-label">{{ $t("marketing.referral.field.ends_at") }}</label>
                <el-date-picker v-model="form.ends_local" type="datetime" value-format="yyyy-MM-ddTHH:mm:ss" format="yyyy-MM-dd HH:mm" class="w-100" clearable :disabled="disabled" :placeholder="$t('marketing.referral.datetime_placeholder')"></el-date-picker>
                <small v-if="errors.ends_at" class="text-danger">{{ err(errors.ends_at) }}</small>
            </div>
        </div>

        <div class="form-group mb-0">
            <label class="form-control-label">{{ $t("marketing.referral.field.note") }}</label>
            <el-input v-model="form.note" type="textarea" :rows="2" maxlength="500" :disabled="disabled"></el-input>
            <small v-if="errors.note" class="text-danger">{{ err(errors.note) }}</small>
        </div>
    </div>
</template>

<script>
import { Select, Option, RadioGroup, RadioButton, Radio, DatePicker, Input } from "element-ui";
import { templateLabel } from "@/utils/referral";

// §5.3 items 4–10 (team … note) — shared by the create/edit and bulk dialogs.
// `form` is the parent's reactive object (fields edited in place).
export default {
    name: "ReferralSharedFields",
    components: {
        [Select.name]: Select,
        [Option.name]: Option,
        [RadioGroup.name]: RadioGroup,
        [RadioButton.name]: RadioButton,
        [Radio.name]: Radio,
        [DatePicker.name]: DatePicker,
        [Input.name]: Input
    },
    props: {
        form: { type: Object, required: true },
        errors: { type: Object, default: () => ({}) },
        teams: { type: Array, default: () => [] },
        campaigns: { type: Array, default: () => [] },
        lps: { type: Array, default: () => [] },
        templates: { type: Array, default: () => [] },
        // The code's current reward when editing: {reward_type, reward_coupon_template_id, reward_coupon}
        original: { type: Object, default: null },
        disabled: { type: Boolean, default: false }
    },
    computed: {
        selectedTemplate() {
            return this.templates.find((t) => t.id === this.form.reward_coupon_template_id) || null;
        },
        // The saved template no longer passes the picker filter → keep it visible (labelled) rather than blank.
        deadTemplate() {
            const o = this.original;
            return !!(o && o.reward_coupon && this.form.reward_coupon_template_id === o.reward_coupon_template_id && !this.selectedTemplate);
        },
        templateOptions() {
            const opts = this.templates.map((t) => ({ id: t.id, label: templateLabel(t, (k, p) => this.$t(k, p)) }));
            const o = this.original;
            if (o && o.reward_coupon && !this.templates.some((t) => t.id === o.reward_coupon_template_id)) {
                opts.unshift({ id: o.reward_coupon_template_id, label: `${o.reward_coupon.name_th || o.reward_coupon.code} · ${this.$t("marketing.referral.hint.template_dead")}` });
            }
            return opts;
        },
        rewardChanged() {
            const o = this.original;
            if (!o) return false;
            if (o.reward_type !== this.form.reward_type) return true;
            return this.form.reward_type === "coupon" && o.reward_coupon_template_id !== this.form.reward_coupon_template_id;
        }
    },
    methods: {
        err(rule) {
            const k = "marketing.referral.err." + rule;
            return this.$te(k) ? this.$t(k) : this.$t("marketing.referral.err.format");
        }
    }
};
</script>
